import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigationType } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import "./App.css";
import { LanguageProvider } from "./context/LanguageContext";
import Landing from "./pages/Landing";
import BlogPage from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import CityPage from "./pages/CityPage";
import Admin from "./pages/Admin";
import GuidePage from "./pages/GuidePage";
import OffensesPage from "./pages/Offenses";
import NotFound from "./pages/NotFound";
import Chatbot from "./components/Chatbot";
import WhatsAppFloat from "./components/WhatsAppFloat";
import MobileCallBar from "./components/MobileCallBar";
import { Toaster } from "sonner";
import { initGA, trackPageview, installCallTracker } from "./lib/analytics";

function ScrollReveal() {
  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    document.querySelectorAll(".reveal").forEach((el) => io.observe(el));
    return () => io.disconnect();
  });
  return null;
}

// Reset scroll on every link click (incl. same-route clicks like Blog/Knowledge Hub),
// so the hub always opens at its heading/search instead of mid-grid or in the footer.
// Back/forward (POP) and #hash links keep their existing behaviour.
function ScrollToTopOnNavigate() {
  const loc = useLocation();
  const navType = useNavigationType();
  useEffect(() => {
    if (navType === "POP" || loc.hash) return;
    window.scrollTo(0, 0);
  }, [loc.key, loc.hash, navType]);
  return null;
}

function AnalyticsRouterHook() {
  const loc = useLocation();
  useEffect(() => {
    // Fire pageview shortly after route change so Helmet has updated the title
    const id = setTimeout(() => {
      trackPageview(loc.pathname + loc.search, document.title);
    }, 250);
    return () => clearTimeout(id);
  }, [loc.pathname, loc.search]);
  return null;
}

export default function App() {
  useEffect(() => {
    initGA();
    installCallTracker();
  }, []);

  return (
    <HelmetProvider>
      <LanguageProvider>
        <div className="App">
          <BrowserRouter>
            <ScrollReveal />
            <ScrollToTopOnNavigate />
            <AnalyticsRouterHook />
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/category/:catSlug" element={<BlogPage />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/offenses" element={<OffensesPage />} />
              <Route path="/guide" element={<GuidePage />} />
              <Route path="/guide/:slug" element={<GuidePage />} />
              <Route path="/bail-lawyer-delhi" element={<CityPage />} />
              <Route path="/bail-lawyer-allahabad" element={<CityPage />} />
              <Route path="/bail-lawyer-lucknow" element={<CityPage />} />
              <Route path="/bail-lawyer-patna" element={<CityPage />} />
              <Route path="/bail-lawyer-bangalore" element={<CityPage />} />
              <Route path="/bail-lawyer-kolkata" element={<CityPage />} />
              <Route path="/bail-lawyer-guwahati" element={<CityPage />} />
              <Route path="/bail-lawyer-mumbai" element={<CityPage />} />
              <Route path="/bail-lawyer-chennai" element={<CityPage />} />
              <Route path="/bail-lawyer-hyderabad" element={<CityPage />} />
              <Route path="/bail-lawyer-jaipur" element={<CityPage />} />
              <Route path="/bail-lawyer-ahmedabad" element={<CityPage />} />
              <Route path="/bail-lawyer-bhopal" element={<CityPage />} />
              <Route path="/bail-lawyer-chandigarh" element={<CityPage />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
            <Chatbot />
            <WhatsAppFloat />
            <MobileCallBar />
            <Toaster position="top-center" richColors />
          </BrowserRouter>
        </div>
      </LanguageProvider>
    </HelmetProvider>
  );
}
